import React from "react";
import { Input, Label } from "reactstrap";
import Select from "react-select";
import { customFieldsCopy } from "./copy";
import type { ResolvedFieldConfig, TaxonomyOption } from "./types";
import { activeOptions } from "./values";
import RichContentFieldInput from "./RichContentFieldInput";

type Props = {
  field: ResolvedFieldConfig;
  id?: string;
  value: unknown;
  onChange: (taxonomyId: string, value: unknown) => void;
  invalid?: boolean;
  readOnly?: boolean;
  productId?: string;
};

function toSelectValue(options: TaxonomyOption[], value: unknown, multi: boolean) {
  if (multi) {
    const ids = Array.isArray(value) ? value.map(String) : [];
    return ids.map((id) => ({ value: id, label: options.find((opt) => opt.id === id)?.label || id }));
  }
  const id = value == null ? "" : String(value);
  const match = options.find((opt) => opt.id === id);
  return id ? { value: id, label: match?.label || id } : null;
}

const TaxonomyFieldRenderer = ({ field, value, onChange, invalid, readOnly, id, productId }: Props) => {
  const fieldId = id || `hkp-tax-${field.taxonomyId}`;
  const options = activeOptions(field.options);
  const mark = field.requiredOnPublish || field.requiredInProcess ? " *" : "";

  return (
    <div className="hkp-tax-field" id={fieldId}>
      <Label htmlFor={`${fieldId}-input`}>
        {field.name}
        {mark}
      </Label>
      {field.description ? <p className="text-muted small mb-1">{field.description}</p> : null}
      {field.fieldType === "TEXT" ? (
        <Input
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${fieldId}-error` : undefined}
          id={`${fieldId}-input`}
          value={value == null ? "" : String(value)}
          disabled={readOnly}
          invalid={invalid}
          onChange={(e) => onChange(field.taxonomyId, e.target.value)}
        />
      ) : null}
      {field.fieldType === "NUMBER" ? (
        <div className="hkp-tax-number">
          <Input
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? `${fieldId}-error` : undefined}
            id={`${fieldId}-input`}
            type="number"
            value={value == null || value === "" ? "" : String(value)}
            disabled={readOnly}
            invalid={invalid}
            onChange={(e) => {
              const raw = e.target.value;
              onChange(field.taxonomyId, raw === "" ? "" : Number(raw));
            }}
          />
          {field.unit ? <span className="hkp-tax-unit">{field.unit}</span> : null}
        </div>
      ) : null}
      {field.fieldType === "DATE" ? (
        <Input
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${fieldId}-error` : undefined}
          id={`${fieldId}-input`}
          type="date"
          value={value == null ? "" : String(value)}
          disabled={readOnly}
          invalid={invalid}
          onChange={(e) => onChange(field.taxonomyId, e.target.value)}
        />
      ) : null}
      {field.fieldType === "BOOLEAN" ? (
        <Select
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${fieldId}-error` : undefined}
          inputId={`${fieldId}-input`}
          classNamePrefix="react-select"
          className="React_selector"
          isDisabled={readOnly}
          placeholder="Select"
          value={
            value === true
              ? { value: "true", label: "Yes" }
              : value === false
                ? { value: "false", label: "No" }
                : null
          }
          options={[
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
          onChange={(opt: any) => {
            if (!opt) {
              onChange(field.taxonomyId, "");
              return;
            }
            onChange(field.taxonomyId, opt.value === "true");
          }}
          isClearable
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          menuPosition="fixed"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
        />
      ) : null}
      {field.fieldType === "SINGLE_SELECT" || field.fieldType === "MULTI_SELECT" ? (
        <Select
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${fieldId}-error` : undefined}
          inputId={`${fieldId}-input`}
          classNamePrefix="react-select"
          className="React_selector"
          isDisabled={readOnly}
          isMulti={field.fieldType === "MULTI_SELECT"}
          placeholder="Select"
          value={toSelectValue(field.options || [], value, field.fieldType === "MULTI_SELECT")}
          options={options.map((opt) => ({ value: opt.id, label: opt.label }))}
          onChange={(opt: any) => {
            if (field.fieldType === "MULTI_SELECT") {
              onChange(
                field.taxonomyId,
                Array.isArray(opt) ? opt.map((row) => row.value) : []
              );
              return;
            }
            onChange(field.taxonomyId, opt?.value || "");
          }}
          isClearable
          isSearchable
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          menuPosition="fixed"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
        />
      ) : null}
      {field.fieldType === "RICH_CONTENT" ? (
        <RichContentFieldInput
          value={value}
          productId={productId}
          readOnly={readOnly}
          onChange={(next) => onChange(field.taxonomyId, next)}
        />
      ) : null}
      {invalid ? (
        <p id={`${fieldId}-error`} className="RequiredField">{customFieldsCopy.requiredOnPublish}</p>
      ) : null}
    </div>
  );
};

export default TaxonomyFieldRenderer;
